<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  stages: {
    type: Array,
    default: () => [],
  },
  selectedStageId: {
    type: [Number, String, null],
    default: null,
  },
  fitContent: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['select']);

const { t } = useI18n();

const lifecycleStages = computed(() =>
  props.stages.filter(stage => stage.category === 'lifecycle_stage')
);

const lostStages = computed(() =>
  props.stages.filter(stage => stage.category === 'lost_stage')
);

const isSelected = stageId => Number(props.selectedStageId) === Number(stageId);

const selectStage = stageId => {
  emit('select', stageId);
};

const rootClasses = computed(() =>
  props.fitContent
    ? 'flex w-max flex-col px-1.5 pb-1'
    : 'flex min-w-[272px] flex-col pb-1'
);

const sectionHeaderClasses =
  'px-3.5 pb-1.5 text-sm font-semibold text-n-slate-11';

const firstSectionHeaderClasses = `${sectionHeaderClasses} pt-2`;

const itemClasses = computed(() =>
  props.fitContent
    ? 'flex items-center gap-2 rounded-lg px-3.5 py-2 text-left text-sm text-n-slate-12 transition-colors hover:bg-n-alpha-2'
    : 'flex w-full items-center gap-2 rounded-lg px-3.5 py-2 text-left text-sm text-n-slate-12 transition-colors hover:bg-n-alpha-2'
);

const labelClasses = computed(() =>
  props.fitContent
    ? 'font-medium whitespace-nowrap'
    : 'min-w-0 flex-1 truncate font-medium'
);

const clearItemClasses = computed(() =>
  props.fitContent
    ? 'flex items-center rounded-lg px-3.5 py-2 text-left text-sm font-medium text-n-slate-11 transition-colors hover:bg-n-alpha-2 hover:text-n-slate-12'
    : 'flex w-full items-center rounded-lg px-3.5 py-2 text-left text-sm font-medium text-n-slate-11 transition-colors hover:bg-n-alpha-2 hover:text-n-slate-12'
);

const clearLabelClasses = computed(() =>
  props.fitContent ? 'whitespace-nowrap' : 'min-w-0 flex-1 truncate'
);

const selectedItemClasses = 'bg-n-alpha-2';
</script>

<template>
  <div :class="rootClasses">
    <div v-if="lifecycleStages.length">
      <p :class="firstSectionHeaderClasses">
        {{ t('LIFECYCLE_STAGES.COLUMNS.LIFECYCLE.TITLE') }}
      </p>

      <button
        v-for="stage in lifecycleStages"
        :key="stage.id"
        type="button"
        :class="[itemClasses, isSelected(stage.id) ? selectedItemClasses : '']"
        @click.stop="selectStage(stage.id)"
      >
        <span
          class="inline-flex size-4.5 shrink-0 items-center justify-center text-[15px] leading-none"
        >
          {{ stage.icon }}
        </span>
        <span :class="labelClasses">
          {{ stage.name }}
        </span>
      </button>
    </div>

    <div v-if="lostStages.length" class="pt-1.5">
      <p :class="sectionHeaderClasses">
        {{ t('LIFECYCLE_STAGES.COLUMNS.LOST.TITLE') }}
      </p>

      <button
        v-for="stage in lostStages"
        :key="stage.id"
        type="button"
        :class="[itemClasses, isSelected(stage.id) ? selectedItemClasses : '']"
        @click.stop="selectStage(stage.id)"
      >
        <span
          class="inline-flex size-4.5 shrink-0 items-center justify-center text-[15px] leading-none"
        >
          {{ stage.icon }}
        </span>
        <span :class="labelClasses">
          {{ stage.name }}
        </span>
      </button>
    </div>

    <div class="mt-1.5 border-t border-n-weak pt-1.5">
      <button
        type="button"
        :class="[
          clearItemClasses,
          selectedStageId === null ? selectedItemClasses : '',
        ]"
        @click.stop="selectStage(null)"
      >
        <span :class="clearLabelClasses">
          {{ t('LIFECYCLE_STAGES.CONTACT_FIELD.CLEAR_SELECTION') }}
        </span>
      </button>
    </div>
  </div>
</template>
